/**
 * Typed access to the H5P editor globals (it runs in the Angular window, not
 * an iframe). Only what the translation layer touches is declared.
 */
import { H5PParams, H5PSemantics, TranslationValue } from './walker';

export interface H5pProgressCount {
  translated: number;
  total: number;
}

/** The jQuery-side translation controller (h5peditor-translation-mode.js). */
export interface H5pTranslationModeApi {
  enter(options: {
    language: string;
    map: Record<string, TranslationValue>;
    onMapChange?: () => void;
    /**
     * Fired on an edit of an untranslated field — that write lands in the
     * source params and so hits every language. Latches until the host calls
     * `universalEditHandled` (older bundles never re-arm: once per session).
     */
    onUniversalEdit?: () => void;
  }): void;
  exit(): void;
  /**
   * Dialog outcome for the last reported universal edit: `false` restores the
   * control's pre-change value, `true` keeps it. Always re-arms the watch.
   */
  universalEditHandled?(confirmed: boolean): void;
  isActive(): boolean;
  getLanguage(): string | null;
  flush(): void;
  updateProgress(perItem: Record<string, H5pProgressCount>): void;
  /**
   * Per-wizard-page counts in tab order. `null` for a page with nothing
   * translatable — it carries no tag, since 0/0 would read as "complete".
   */
  updatePageProgress(perPage: ReadonlyArray<H5pProgressCount | null>): void;
  /** Localized words for the status pills (the jQuery layer has no i18n). */
  setStatusLabels(labels: Record<string, string>): void;
  /** Drag the Words parity messages ({{expected}} / {{actual}} placeholders).
   * Optional: absent in an older editor bundle still loaded in the page. */
  setParityLabels?(labels: Record<string, string>): void;
}

interface H5pLibraryCacheEntry {
  semantics?: H5PSemantics;
}

export interface H5pEditorNamespace {
  libraryCache?: Record<string, H5pLibraryCacheEntry>;
  TranslationMode?: H5pTranslationModeApi;
  loadLibrary?: (
    uberName: string,
    callback: (semantics: H5PSemantics | undefined) => void,
  ) => void;
}

export interface H5pEditorForm {
  params: H5PParams;
  metadata?: Record<string, unknown>;
}

export interface H5pEditorSelector {
  form?: H5pEditorForm;
  getCurrentLibrary?: () => string;
}

export interface H5pEditorInstance {
  selector?: H5pEditorSelector;
}

interface H5pWindow {
  H5PEditor?: H5pEditorNamespace;
  h5peditor?: H5pEditorInstance;
  H5PIntegration?: { url?: string };
}

const h5pWindow = (): H5pWindow => globalThis as unknown as H5pWindow;

export const getH5pEditorNamespace = (): H5pEditorNamespace | undefined =>
  h5pWindow().H5PEditor;

export const getH5pTranslationMode = (): H5pTranslationModeApi | undefined =>
  h5pWindow().H5PEditor?.TranslationMode;

/** The live editor form of the currently open module (params + metadata). */
export const getH5pEditorForm = (): H5pEditorForm | undefined =>
  h5pWindow().h5peditor?.selector?.form;

export const getH5pCurrentLibrary = (): string | undefined =>
  h5pWindow().h5peditor?.selector?.getCurrentLibrary?.();

/** Base URL of the h5p-service (used by the existing save path too). */
export const getH5pServiceUrl = (): string =>
  h5pWindow().H5PIntegration?.url ?? '';
